import { useLayoutEffect, useRef, useState } from "react";
import type { RichBlock, RichRun } from "../../shared/document-line-content";
import { DocumentEditableText } from "./DocumentEditableText";

function renderRuns(runs: RichRun[]) {
  return runs.map((run, index) => {
    let value = <>{run.text}</>;
    if (run.bold) value = <strong>{value}</strong>;
    if (run.italic) value = <em>{value}</em>;
    if (run.underline) value = <u>{value}</u>;
    return <span key={index}>{value}</span>;
  });
}

export function RichDocumentText({ blocks }: { blocks: RichBlock[] }) {
  return <div className="document-rich-text">{blocks.map((block, index) => block.type === "paragraph"
    ? <p key={index}>{renderRuns(block.runs)}</p>
    : block.type === "unordered_list" ? <ul key={index}>{block.items.map((item, itemIndex) => <li key={itemIndex}>{renderRuns(item)}</li>)}</ul>
    : <ol key={index}>{block.items.map((item, itemIndex) => <li key={itemIndex}>{renderRuns(item)}</li>)}</ol>)}</div>;
}

function domRuns(node: Node, style: Pick<RichRun, "bold" | "italic" | "underline"> = {}): RichRun[] {
  if (node.nodeType === Node.TEXT_NODE) return node.textContent ? [{ text: node.textContent, ...style }] : [];
  if (!(node instanceof Element) || ["SCRIPT", "STYLE", "IFRAME", "IMG", "VIDEO", "AUDIO"].includes(node.tagName)) return [];
  const next = { ...style, ...(["B", "STRONG"].includes(node.tagName) ? { bold: true } : {}), ...(["I", "EM"].includes(node.tagName) ? { italic: true } : {}), ...(node.tagName === "U" ? { underline: true } : {}) };
  if (node.tagName === "BR") return [{ text: "\n", ...next }];
  return [...node.childNodes].flatMap(child => domRuns(child, next));
}

function blocksFromDom(root: HTMLElement): RichBlock[] {
  const blocks: RichBlock[] = [];
  const collect = (node: Node) => {
    if (node instanceof Element && ["UL", "OL"].includes(node.tagName)) {
      blocks.push({ type: node.tagName === "UL" ? "unordered_list" : "ordered_list", items: [...node.children].filter(item => item.tagName === "LI").map(item => domRuns(item)) });
    } else if (node instanceof Element && (node.classList.contains("document-rich-text") || node.querySelector("ul, ol"))) {
      node.childNodes.forEach(collect);
    } else {
      const runs = domRuns(node);
      if (runs.length) blocks.push({ type: "paragraph", runs });
    }
  };
  root.childNodes.forEach(collect);
  return blocks;
}

function hasMeaningfulDetails(blocks: RichBlock[]) {
  return blocks.some(block => block.type === "paragraph"
    ? block.runs.some(run => Boolean(run.text.trim()))
    : block.items.some(item => item.some(run => Boolean(run.text.trim()))));
}

export function DocumentLineContent({ title, details, editable, lineNumber, onTitleChange, onDetailsChange, onDetailsEdit }: {
  title: string; details: RichBlock[]; editable: boolean; lineNumber: number;
  onTitleChange(value: string): void; onDetailsChange(value: RichBlock[]): void; onDetailsEdit(): void;
}) {
  const detailsRef = useRef<HTMLDivElement>(null);
  const detailsEdited = useRef(false);
  // The browser owns this editable subtree after hydration. Reconciliation of
  // its mutated children on blur can append a second copy of pasted blocks.
  const [editorSeed, setEditorSeed] = useState(() => ({ content: <RichDocumentText blocks={details}/>, revision: 0 }));
  const [active, setActive] = useState(false);
  const [detailsEmpty, setDetailsEmpty] = useState(() => !hasMeaningfulDetails(details));
  useLayoutEffect(() => {
    const element = detailsRef.current;
    if (!editable || !element || document.activeElement === element) return;
    setDetailsEmpty(!hasMeaningfulDetails(details));
    // External restore/reset can rehydrate an inactive editor. Our own edits
    // already match the DOM and must keep its selection and native undo stack.
    if (JSON.stringify(blocksFromDom(element)) !== JSON.stringify(details)) {
      setEditorSeed(seed => ({ content: <RichDocumentText blocks={details}/>, revision: seed.revision + 1 }));
    }
  }, [details, editable]);
  const [activeFormats, setActiveFormats] = useState<string[]>([]);
  const refreshFormats = () => {
    const next = (["bold", "italic", "underline", "insertUnorderedList", "insertOrderedList"] as const).filter(command => document.queryCommandState(command));
    setActiveFormats(current => current.length === next.length && current.every((command, index) => command === next[index]) ? current : next);
  };
  const format = (command: "bold" | "italic" | "underline" | "insertUnorderedList" | "insertOrderedList") => {
    detailsRef.current?.focus();
    document.execCommand(command);
    detailsEdited.current = true;
    onDetailsEdit();
    refreshFormats();
  };
  return <div className="document-line-content">
    <div className="document-line-title">{editable ? <DocumentEditableText label={`Line ${lineNumber} title`} value={title} placeholder="Describe the work" onChange={onTitleChange} maxLength={500}/> : title}</div>
    {editable ? <div className={`document-rich-edit-shell${detailsEmpty ? " document-rich-edit-shell--empty" : ""}`} onFocus={() => setActive(true)} onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget as Node)) { setActive(false); if (detailsEdited.current && detailsRef.current) { detailsEdited.current = false; const blocks = blocksFromDom(detailsRef.current); onDetailsChange(hasMeaningfulDetails(blocks) ? blocks : []); } } }}>
      <div key={editorSeed.revision} ref={detailsRef} data-edit-target contentEditable suppressContentEditableWarning role="textbox" aria-multiline="true" aria-label={`Line ${lineNumber} details`} className="document-rich-editor" data-empty={detailsEmpty} data-placeholder="Add details" onInput={event => { const blocks = blocksFromDom(event.currentTarget); const meaningful = hasMeaningfulDetails(blocks); setDetailsEmpty(!meaningful); detailsEdited.current = true; onDetailsChange(meaningful ? blocks : []); refreshFormats(); }} onKeyUp={refreshFormats} onMouseUp={refreshFormats} onPaste={event => { event.preventDefault(); document.execCommand("insertText", false, event.clipboardData.getData("text/plain")); }}>{editorSeed.content}</div>
      {active ? <div className="document-rich-toolbar" role="toolbar" aria-label={`Line ${lineNumber} formatting`}>{([ ["bold", "B"], ["italic", "I"], ["underline", "U"], ["insertUnorderedList", "• List"], ["insertOrderedList", "1. List"] ] as const).map(([command, label]) => <button key={command} type="button" className="document-rich-action" aria-label={command} aria-pressed={activeFormats.includes(command)} onMouseDown={event => event.preventDefault()} onClick={() => format(command)}>{label}</button>)}</div> : null}
    </div> : details.length ? <RichDocumentText blocks={details}/> : null}
  </div>;
}
